import {
  DownOutlined,
  GlobalOutlined,
  HomeOutlined,
  LeftOutlined,
  ProfileOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { useState, useMemo, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ALL_GROUPS,
  C,
  SHIMMER_STYLE,
  USER1_KEYS,
  USER2_KEYS,
  THEMES,
  hexToRgba,
  DASHBOARD_NAV_STYLE,
} from "./SidebarConst";
import InventoryScreen from "./InventoryScreen";

// External e-catalogue link — opens in a new tab, no in-app routing involved.
const ECAT_URL =
  "https://ecat.timeserasoftware.in/collection?jewellery=GOLD&page=1";

export function getFilteredGroups(userType) {
  if (userType === "ADMIN")
    return ALL_GROUPS.filter((g) => USER1_KEYS.includes(g.key));
  return ALL_GROUPS;
}

// Modules list on the main screen — Masters & Utilities are shown
// separately in the Settings section, so they are excluded here.
export function getModuleGroups(userType) {
  return getFilteredGroups(userType).filter(
    (g) => g.key !== "masters" && g.key !== "utilities",
  );
}

const SectionHeading = ({ label, moduleColor, C }) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      gap: 6,
      padding: "4px 4px 6px",
      marginTop: 2,
    }}
  >
    <span
      style={{
        width: 3,
        height: 12,
        borderRadius: 2,
        background: C.goldAccent,
        flexShrink: 0,
      }}
    />
    <span
      style={{
        fontSize: 10,
        fontWeight: 800,
        color: "#000",
        letterSpacing: "0.08em",
        textTransform: "uppercase",
        fontFamily: "Roboto, sans-serif",
        borderRadius: 4,
        padding: "2px 6px",
        background: `linear-gradient(135deg, #ffe066 0%, #d8b535ee 100%)`,
      }}
    >
      {label}
    </span>
    <div style={{ flex: 1, height: 1, background: C.headerBorder }} />
  </div>
);

const INVENTORY_STYLE_MODULES = [
  "inventory",
  "accounts",
  "pos",
  "crm",
  "top",
  "purchase",
];
const PaletteIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10c.55 0 1-.45 1-1 0-.27-.1-.52-.26-.71-.4-.47-.4-1.15 0-1.58.16-.19.26-.44.26-.71 0-.55-.45-1-1-1H10c-3.31 0-6-2.69-6-6 0-3.87 3.58-7 8-7s8 3.13 8 7c0 1.93-.86 3.68-2.24 4.93-.4.36-.76.79-.76 1.32C17 19.34 17.9 20 19 20c.55 0 1-.45 1-1 0-3.87-3.58-7-8-7zm-5.5 6c-.83 0-1.5.67-1.5 1.5S5.67 11 6.5 11 8 10.33 8 9.5 7.33 8 6.5 8zm3-4c-.83 0-1.5.67-1.5 1.5S8.67 7 9.5 7s1.5-.67 1.5-1.5S10.33 4 9.5 4zm5 0c-.83 0-1.5.67-1.5 1.5S13.67 7 14.5 7s1.5-.67 1.5-1.5S15.33 4 14.5 4zm3 4c-.83 0-1.5.67-1.5 1.5S16.67 11 17.5 11s1.5-.67 1.5-1.5S18.33 8 17.5 8z" />
  </svg>
);
const THEME_SWATCHES = {
  royalBlue: "#5b4fcf",
  slateDark: "#38bdf8",
  midnightRose: "#fb7185",
  warmGray: "#0f766e",
};

const NavigationHeader = ({
  title,
  showBack,
  onBack,
  C: themeC,
  moduleColor,
}) => {
  const colors = themeC || C;
  const mc = moduleColor || colors.accent;
  return (
    <div
      style={{
        height: 44,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        gap: 8,
        padding: "0 10px",
        borderBottom: `1px solid ${hexToRgba(mc, 0.4)}`,
        background: hexToRgba(mc, 0.1),
      }}
    >
      {showBack && (
        <button
          onClick={onBack}
          className="dashboard-nav-arrow"
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 26,
            height: 26,
            borderRadius: 6,
            border: `1px solid ${hexToRgba(mc, 0.5)}`,
            background: hexToRgba(mc, 0.15),
            cursor: "pointer",
            flexShrink: 0,
            color: colors.text,
            fontSize: 10,
          }}
          aria-label="Go back"
        >
          <LeftOutlined />
        </button>
      )}
      <span
        style={{
          flex: 1,
          fontWeight: 700,
          fontSize: 12,
          color: mc,
          fontFamily: "Roboto, sans-serif",
          letterSpacing: "0.03em",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
          textTransform: "uppercase",
        }}
      >
        {title}
      </span>
    </div>
  );
};
const ReportsAccordion = ({ entries, C: themeC }) => {
  const colors = themeC || C;
  const [openCategory, setOpenCategory] = useState(null);
  const location = useLocation();

  if (!entries || entries.length === 0) return null;

  return (
    <div style={{ marginTop: 4 }}>
      {entries.map((entry) => {
        const isOpen = openCategory === entry.category;
        return (
          <div key={entry.category}>
            <button
              onClick={() => setOpenCategory(isOpen ? null : entry.category)}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                width: "calc(100% - 16px)",
                margin: "1px 8px",
                padding: "7px 8px",
                background: isOpen ? colors.accentBg : "transparent",
                border: "none",
                borderRadius: 7,
                cursor: "pointer",
                transition: "background 0.12s",
              }}
              onMouseEnter={(e) => {
                if (!isOpen) e.currentTarget.style.background = colors.bgHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = isOpen
                  ? colors.accentBg
                  : "transparent";
              }}
            >
              <span
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  width: 22,
                  height: 22,
                  borderRadius: 5,
                  background: isOpen
                    ? colors.accentBg
                    : "rgba(255,255,255,0.08)",
                  color: isOpen ? colors.accent : colors.textMuted,
                  fontSize: 11,
                  flexShrink: 0,
                }}
              >
                <ProfileOutlined />
              </span>
              <span
                style={{
                  flex: 1,
                  fontSize: 11,
                  fontWeight: 500,
                  color: isOpen ? colors.accent : colors.textMuted,
                  fontFamily: "Roboto, sans-serif",
                  textAlign: "left",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {entry.category}
              </span>
              <span
                style={{
                  fontSize: 9,
                  color: colors.textMuted,
                  transition: "transform 0.2s",
                  transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}
              >
                <DownOutlined />
              </span>
            </button>

            {/* Report links */}
            {isOpen && (
              <div style={{ padding: "2px 0 4px" }}>
                {entry.reports.map((report) => {
                  const active = location.pathname === report.path;
                  return (
                    <Link
                      key={report.path}
                      to={report.path}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 8,
                        padding: "6px 12px 6px 44px",
                        margin: "1px 8px",
                        borderRadius: 7,
                        textDecoration: "none",
                        background: active ? colors.bgActive : "transparent",
                        transition: "background 0.12s",
                        position: "relative",
                      }}
                      onMouseEnter={(e) => {
                        if (!active)
                          e.currentTarget.style.background = colors.bgHover;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.background = active
                          ? colors.bgActive
                          : "transparent";
                      }}
                    >
                      {active && (
                        <span
                          style={{
                            position: "absolute",
                            left: 8,
                            top: "50%",
                            transform: "translateY(-50%)",
                            width: 3,
                            height: 14,
                            borderRadius: 2,
                            background: colors.accent,
                          }}
                        />
                      )}
                      <span
                        style={{
                          width: active ? 5 : 4,
                          height: active ? 5 : 4,
                          borderRadius: "50%",
                          background: active ? colors.accent : colors.textMuted,
                          flexShrink: 0,
                        }}
                      />
                      <span
                        style={{
                          fontSize: 11.5,
                          color: active ? colors.textActive : colors.textMuted,
                          fontFamily: "Roboto, sans-serif",
                          fontWeight: active ? 600 : 400,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {report.label}
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

const ThemePicker = ({ activeThemeId, onSelect, C: T }) => {
  const [open, setOpen] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <button
        onClick={() => setOpen((v) => !v)}
        title="Change sidebar theme"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          gap: 6,
          width: "100%",
          padding: "10px 12px",
          borderRadius: 10,
          border: `2px solid ${T.accent}`,
          background: `linear-gradient(135deg, ${T.pickerBg}, ${T.pickerBgHover})`,
          cursor: "pointer",
          color: T.text,
          fontSize: 14,
          fontFamily: "Roboto,sans-serif",
          fontWeight: 700,
          letterSpacing: "0.04em",
          boxShadow: `
      0 0 12px ${T.accent},
      0 0 24px ${T.accent}55
    `,
          transition: "all 0.25s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.transform = "scale(1.03)";
          e.currentTarget.style.boxShadow = `
      0 0 16px ${T.accent},
      0 0 32px ${T.accent}88
    `;
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.transform = "scale(1)";
          e.currentTarget.style.boxShadow = `
      0 0 12px ${T.accent},
      0 0 24px ${T.accent}55
    `;
        }}
      >
        <PaletteIcon />
        <span>Theme</span>

        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: "50%",
            background: THEME_SWATCHES[activeThemeId],
            boxShadow: `0 0 10px ${THEME_SWATCHES[activeThemeId]}`,
            flexShrink: 0,
          }}
        />
      </button>
      {open && (
        <div
          style={{
            position: "absolute",
            bottom: "calc(100% + 6px)",
            left: 0,
            right: 0,
            background: T.pickerDropdownBg,
            border: `1px solid ${T.headerBorder}`,
            borderRadius: 10,
            overflow: "hidden",
            boxShadow: "0 8px 32px rgba(0,0,0,0.25)",
            zIndex: 999,
          }}
        >
          {Object.values(THEMES).map((t) => {
            const sel = t.id === activeThemeId;
            return (
              <button
                key={t.id}
                onClick={() => {
                  onSelect(t.id);
                  setOpen(false);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  width: "100%",
                  padding: "9px 14px",
                  background: sel ? T.pickerItemSelectedBg : "transparent",
                  border: "none",
                  borderBottom: `1px solid ${T.pickerItemBorder}`,
                  cursor: "pointer",
                  transition: "background 0.15s",
                }}
                onMouseEnter={(e) => {
                  if (!sel)
                    e.currentTarget.style.background = T.pickerItemHoverBg;
                }}
                onMouseLeave={(e) => {
                  if (!sel) e.currentTarget.style.background = "transparent";
                }}
              >
                <span
                  style={{
                    width: 13,
                    height: 13,
                    borderRadius: "50%",
                    background: THEME_SWATCHES[t.id],
                    boxShadow: `0 0 7px ${THEME_SWATCHES[t.id]}`,
                    flexShrink: 0,
                  }}
                />
                <span
                  style={{
                    fontSize: 12,
                    fontWeight: sel ? 700 : 500,
                    color: sel ? T.accent : T.pickerItemText,
                    fontFamily: "Roboto,sans-serif",
                  }}
                >
                  {t.label}
                </span>
                {sel && (
                  <span
                    style={{
                      marginLeft: "auto",
                      color: T.accent,
                      fontSize: 11,
                    }}
                  >
                    ✓
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

const ReportCategoryAccordion = ({
  entry,
  moduleColor,
  isActive,
  C,
  openAccordion,
  toggleAccordion,
}) => {
  const isOpen = openAccordion === entry.key;
  const hasActive = (entry.items || []).some((i) => isActive(i.path));

  return (
    <div style={{ marginBottom: 4 }}>
      <button
        onClick={() => toggleAccordion(entry.key)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          width: "100%",
          padding: "8px 10px",
          background:
            isOpen || hasActive
              ? hexToRgba(moduleColor, 0.18)
              : "rgba(255,255,255,0.04)",
          border: `1px solid ${isOpen || hasActive
              ? hexToRgba(moduleColor, 0.35)
              : "rgba(255,255,255,0.10)"
            }`,
          borderRadius: 8,
          cursor: "pointer",
          transition: "background 0.2s ease, border-color 0.2s ease",
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.background =
            isOpen || hasActive
              ? hexToRgba(moduleColor, 0.28)
              : "rgba(255,255,255,0.09)";
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.background =
            isOpen || hasActive
              ? hexToRgba(moduleColor, 0.18)
              : "rgba(255,255,255,0.04)";
        }}
      >
        <span
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: 28,
            height: 28,
            borderRadius: 7,
            background:
              isOpen || hasActive
                ? `linear-gradient(135deg, ${hexToRgba(moduleColor, 0.95)}, ${moduleColor})`
                : hexToRgba(moduleColor, 0.18),
            color: isOpen || hasActive ? "#FFFFFF" : moduleColor,
            fontSize: 15,
            flexShrink: 0,
            boxShadow:
              isOpen || hasActive
                ? `0 2px 8px ${hexToRgba(moduleColor, 0.5)}`
                : "none",
            border: `1px solid ${isOpen || hasActive ? "transparent" : hexToRgba(moduleColor, 0.3)
              }`,
            transition:
              "background 0.2s ease, box-shadow 0.2s ease, color 0.2s ease",
          }}
        >
          {entry.icon || <ProfileOutlined />}
        </span>
        <span
          style={{
            flex: 1,
            fontSize: 13,
            fontWeight: 700,
            color: isOpen || hasActive ? moduleColor : C.text,
            fontFamily: "Roboto, sans-serif",
            textAlign: "left",
          }}
        >
          {entry.label}
        </span>
        <span
          style={{
            fontSize: 11,
            color: isOpen || hasActive ? moduleColor : C.textMuted,
            transition: "transform 0.2s",
            transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
          }}
        >
          <DownOutlined />
        </span>
      </button>

      {isOpen && (
        <ul
          style={{
            listStyle: "none",
            margin: "6px 0 0",
            padding: "0 0 0 18px",
            position: "relative",
            display: "flex",
            flexDirection: "column",
            gap: 6,
          }}
        >
          <span
            style={{
              position: "absolute",
              left: 7,
              top: 0,
              bottom: 10,
              width: 2,
              borderRadius: 2,
              background: hexToRgba(moduleColor, 0.3),
            }}
          />

          {(entry.items || []).map((report) => {
            const active = isActive(report.path);
            return (
              <li key={report.path} style={{ position: "relative" }}>
                <span
                  style={{
                    position: "absolute",
                    left: -11,
                    top: "50%",
                    width: 10,
                    height: 2,
                    background: hexToRgba(moduleColor, 0.3),
                  }}
                />
                <Link
                  to={report.path}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 9,
                    padding: "7px 10px",
                    borderRadius: 7,
                    textDecoration: "none",
                    background: active
                      ? hexToRgba(moduleColor, 0.75)
                      : hexToRgba(moduleColor, 0.4),
                    border: `1.5px solid ${active ? moduleColor : hexToRgba(moduleColor, 0.25)
                      }`,
                    boxShadow: active
                      ? `0 0 0 1px ${hexToRgba(moduleColor, 0.4)}, 0 4px 12px ${hexToRgba(moduleColor, 0.45)}`
                      : "none",
                    position: "relative",
                    transition:
                      "background 0.18s ease, border-color 0.18s ease, transform 0.2s cubic-bezier(0.4,0,0.2,1), box-shadow 0.2s ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.background = active
                      ? hexToRgba(moduleColor, 0.85)
                      : hexToRgba(moduleColor, 0.32);
                    e.currentTarget.style.transform = "translateX(3px)";
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.background = active
                      ? hexToRgba(moduleColor, 0.75)
                      : hexToRgba(moduleColor, 0.4);
                    e.currentTarget.style.transform = "translateX(0)";
                  }}
                >
                  {active && (
                    <span
                      style={{
                        position: "absolute",
                        left: -16,
                        top: "50%",
                        transform: "translateY(-50%)",
                        width: 3,
                        height: 16,
                        borderRadius: "0 3px 3px 0",
                        background: moduleColor,
                        boxShadow: `0 0 6px ${hexToRgba(moduleColor, 0.8)}`,
                      }}
                    />
                  )}
                  <span
                    style={{
                      width: active ? 7 : 5,
                      height: active ? 7 : 5,
                      borderRadius: "50%",
                      background: active ? "#000" : "rgb(255, 255, 255)",
                      flexShrink: 0,
                      boxShadow: active
                        ? "0 0 6px rgba(255,255,255,0.7)"
                        : "none",
                      transition:
                        "background 0.18s ease, width 0.18s ease, height 0.18s ease",
                    }}
                  />
                  <span
                    className="rep-text"
                    style={{
                      fontSize: 13,
                      fontWeight: active ? 800 : 500,
                      color: active ? "#000" : "rgb(255, 255, 255)",
                      fontFamily: "Roboto, sans-serif",
                      letterSpacing: active ? "0.02em" : "0",
                      lineHeight: 1.35,
                      transition: "color 0.18s ease",
                    }}
                  >
                    {report.label}
                  </span>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const DASHBOARD_COLOR = "#d8b535ee";
const DASHBOARD_LIGHT = "#ffe066";
const DASHBOARD_DARK = "#3d2c02";
const Sidebar = ({ onScreenChange } = {}) => {
  const location = useLocation();
  const navigate = useNavigate();

  let userType = Number(localStorage.getItem("adminType") || "");

  const [screen, setScreen] = useState("main");
  const [selectedModule, setSelectedModule] = useState(null);
  const [themeId, setThemeId] = useState(
    () => localStorage.getItem("sidebarTheme") || "royalBlue",
  );
  const C = THEMES[themeId] || THEMES.royalBlue;
  const [transitioning, setTransitioning] = useState(false);
  const [direction, setDirection] = useState("forward");
  const [openAccordion, setOpenAccordion] = useState(null);

  const toggleAccordion = (key) => {
    setOpenAccordion((prev) => (prev === key ? null : key));
  };
  const handleThemeSelect = (id) => {
    setThemeId(id);
    localStorage.setItem("sidebarTheme", id);
  };

  useEffect(() => {
    if (screen === "module" && !selectedModule) {
      setScreen("main");
    }
  }, [screen, selectedModule]);

  const navigateTo = (nextScreen, moduleKey = null) => {
    if (transitioning) return;
    setDirection(nextScreen === "main" ? "backward" : "forward");
    setTransitioning(true);
    setScreen(nextScreen);
    setSelectedModule(moduleKey);
    onScreenChange?.(nextScreen);
    setTimeout(() => setTransitioning(false), 250);
  };

  const goBack = () => navigateTo("main", null);
  const isActive = (path) => location.pathname === path;

  const moduleGroups = getModuleGroups(userType);

  // Masters & Utilities — rendered as the two "Settings" tiles
  const settingsGroups = getFilteredGroups(userType).filter(
    (g) => g.key === "masters" || g.key === "utilities",
  );

  const routeToModule = useMemo(() => {
    const map = {};

    // include settings groups so the active tile highlights correctly
    [...moduleGroups, ...settingsGroups].forEach((group) => {
      group.items?.forEach((item) => {
        map[item.path] = group.key;
      });

      group.subGroups?.forEach((sub) => {
        sub.items?.forEach((item) => {
          map[item.path] = group.key;
        });
      });

      group.accordionGroups?.forEach((acc) => {
        acc.items?.forEach((item) => {
          map[item.path] = group.key;
        });
      });

      group.moduleReports?.forEach((rep) => {
        rep.items?.forEach((item) => {
          map[item.path] = group.key;
        });
      });
    });

    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userType]);

  const activeModuleKey =
    screen === "module-reports" && selectedModule
      ? selectedModule
      : routeToModule[location.pathname];
  const selectedModuleGroup = selectedModule
    ? ALL_GROUPS.find((g) => g.key === selectedModule)
    : null;

  const screenStyle = {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minHeight: 0,
    background: C.bg,
    transform: transitioning
      ? direction === "forward"
        ? "translateX(100%)"
        : "translateX(-100%)"
      : "translateX(0)",
    opacity: transitioning ? 0 : 1,
    transition: "transform 250ms ease, opacity 250ms ease",
    padding: "16px 4px",
  };

  const TileItem = ({ label, icon, path: itemPath, moduleColor }) => {
    const [hovered, setHovered] = useState(false);
    const active = isActive(itemPath);
    const mc = moduleColor || C.accent;
    return (
      <Link
        to={itemPath}
        className="shimmer-item"
        style={{
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "start",
          gap: 8,
          padding: "10px",
          borderRadius: 8,
          textDecoration: "none",
          border: active
            ? `1px solid ${mc}`
            : hovered
              ? `1px solid ${mc}`
              : `1px solid #ffffff`,
          background: active
            ? hexToRgba(mc, 0.32)
            : hovered
              ? hexToRgba(mc, 0.24)
              : hexToRgba(mc, 0.12),
          boxShadow:
            active || hovered ? `0 4px 14px ${hexToRgba(mc, 0.3)}` : "none",
          cursor: "pointer",
          transition: "all 0.25s ease",
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <span
          className="module-icon"
          style={{
            fontSize: 17,
            color: active ? "#ffffff" : mc,
            display: "flex",
            alignItems: "center",
            lineHeight: 1,
            transform: hovered ? "scale(1.12)" : "scale(1)",
            transition: "transform 0.25s ease",
          }}
        >
          {icon}
        </span>
        <span
          className="module-label"
          style={{
            fontSize: 12,
            fontWeight: active ? 700 : hovered ? 700 : 500,
            color: active ? C.textActive : hovered ? C.textActive : C.text,
            fontFamily: "Roboto, sans-serif",
            textAlign: "left",
            lineHeight: 1.25,
            wordBreak: "break-word",
          }}
        >
          {label}
        </span>
        {active && (
          <span
            style={{
              marginLeft: "auto",
              width: 6,
              height: 6,
              borderRadius: "50%",
              background: mc,
              boxShadow: `0 0 8px ${mc}`,
              flexShrink: 0,
            }}
          />
        )}
      </Link>
    );
  };
  const DashboardStrip = () => {
    return (
      <div style={{ padding: "6px 8px 12px" }}>
        <button
          onClick={() => {
            navigate("/dashboard");
            goBack();
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            width: "100%",
            padding: "5px 18px",
            background: `linear-gradient(135deg, ${DASHBOARD_LIGHT} 0%, ${DASHBOARD_COLOR} 100%)`,
            border: `1px solid ${isActive("/dashboard") ? DASHBOARD_LIGHT : hexToRgba(DASHBOARD_COLOR, 0.7)}`,
            borderRadius: 7,
            cursor: "pointer",
            boxShadow: isActive("/dashboard")
              ? `0 0 14px ${hexToRgba(DASHBOARD_COLOR, 0.55)}`
              : "none",
            transition: "all 0.2s ease",
          }}
        >
          <span
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 30,
              height: 25,
              borderRadius: 6,
              background: hexToRgba(DASHBOARD_DARK, 0.18),
              color: DASHBOARD_DARK,
              fontSize: 16,
              fontWeight: 700,
              flexShrink: 0,
            }}
          >
            <HomeOutlined />
          </span>
          <span
            style={{
              fontSize: 14,
              fontWeight: 700,
              color: DASHBOARD_DARK,
              fontFamily: "Roboto, sans-serif",
            }}
          >
            Dashboard
          </span>
        </button>
      </div>
    );
  };

  if (screen === "main") {
    return (
      <div
        data-screen="main"
        className="sb-shimmer"
        style={{
          ...screenStyle,
          height: "100%",
          overflowY: "auto",
          overflowX: "hidden",
        }}
      >
        <style>{SHIMMER_STYLE}</style>
        <div style={{ padding: "6px 8px 12px" }}>
          <button
            onClick={() => navigate("/dashboard")}
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              width: "100%",
              padding: "5px 18px",
              background: `linear-gradient(135deg, ${DASHBOARD_LIGHT} 0%, ${DASHBOARD_COLOR} 100%)`,
              border: `1px solid ${isActive("/dashboard") ? DASHBOARD_LIGHT : hexToRgba(DASHBOARD_COLOR, 0.7)}`,
              borderRadius: 7,
              cursor: "pointer",
              boxShadow: isActive("/dashboard")
                ? `0 0 14px ${hexToRgba(DASHBOARD_COLOR, 0.55)}`
                : "none",
              transition: "all 0.2s ease",
            }}
          >
            <span
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: 30,
                height: 25,
                borderRadius: 6,
                background: hexToRgba(DASHBOARD_DARK, 0.18),
                color: DASHBOARD_DARK,
                fontSize: 16,
                fontWeight: 700,
                flexShrink: 0,
              }}
            >
              <HomeOutlined />
            </span>
            <span
              style={{
                fontSize: 14,
                fontWeight: 700,
                color: DASHBOARD_DARK,
                fontFamily: "Roboto, sans-serif",
              }}
            >
              Dashboard
            </span>
          </button>
        </div>

        {/* ── MODULES ─────────────────────────────────────────── */}
        <div style={{ padding: "0 4px 16px" }}>
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              padding: "4px 4px 6px",
            }}
          >
            <span
              style={{
                width: 4,
                height: 16,
                borderRadius: 2,
                background: C.goldAccent,
                flexShrink: 0,
              }}
            />

            <span
              style={{
                fontSize: 9,
                fontWeight: 700,
                color: C.accent,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                fontFamily: "Roboto, sans-serif",
                whiteSpace: "nowrap",
              }}
            >
              Modules
            </span>

            <div
              style={{
                flex: 1,
                height: 1,
                background: C.headerBorder,
                marginLeft: 4,
              }}
            />
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 6,
            }}
          >
            {moduleGroups.map((group) => {
              const hasActive = activeModuleKey === group.key;
              return (
                <button
                  key={group.key}
                  className="shimmer-item"
                  onClick={() => navigateTo("module", group.key)}
                  style={{
                    width: "100%",
                    height: 36,
                    display: "flex",
                    alignItems: "center",
                    gap: 4,
                    padding: 0,
                    border: "none",
                    background: "transparent",
                    cursor: "pointer",
                    flexShrink: 0,
                    transition: "transform 0.25s ease-in-out",
                  }}
                  onMouseEnter={(e) => {
                    const label =
                      e.currentTarget.querySelector(".module-label-box");

                    if (!hasActive) {
                      e.currentTarget.style.transform = "scale(1.03)";
                      if (label) {
                        label.style.color = C.textActive;
                      }
                    }
                  }}
                  onMouseLeave={(e) => {
                    const label =
                      e.currentTarget.querySelector(".module-label-box");

                    e.currentTarget.style.transform = "scale(1)";

                    if (!hasActive) {
                      if (label) {
                        label.style.color = "#fff";
                      }
                    }
                  }}
                >
                  {/* icon block */}
                  <span
                    className="module-icon-box"
                    style={{
                      width: 30,
                      height: 30,
                      flexShrink: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: 8,
                      border: `1px solid ${C.accent}`,
                      background: hexToRgba(C.accent, 0.5),
                      fontSize: 15,
                      color: hasActive ? C.iconColorActive : C.iconColor,
                      lineHeight: 1,
                      transition: "all 0.25s ease",
                    }}
                  >
                    {group.icon}
                  </span>

                  {/* label block — fills remaining width */}
                  <span
                    className="module-label-box"
                    style={{
                      flex: 1,
                      minWidth: 0,
                      height: 34,
                      padding: "0 10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "start",
                      borderRadius: 10,
                      border: `1.5px solid ${C.accent}`,
                      background: `linear-gradient(135deg, ${C.pickerBg || C.bgCard}, ${C.pickerBgHover || C.accent})`,
                      fontSize: 13,
                      fontWeight: 700,
                      color: hasActive ? C.textActive : "#fff",
                      fontFamily: "Roboto, sans-serif",
                      letterSpacing: "0.04em",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      transition: "all 0.25s ease",
                    }}
                  >
                    {group.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* ── SETTINGS (Masters + Utilities) ──────────────────── */}
        {settingsGroups.length > 0 && (
          <div style={{ padding: "0 4px 16px" }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "4px 4px 6px",
              }}
            >
              <span
                style={{
                  width: 4,
                  height: 16,
                  borderRadius: 2,
                  background: C.goldAccent,
                  flexShrink: 0,
                }}
              />
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  color: C.accent,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  fontFamily: "Roboto, sans-serif",
                  whiteSpace: "nowrap",
                }}
              >
                Settings
              </span>
              <div
                style={{
                  flex: 1,
                  height: 1,
                  background: C.headerBorder,
                  marginLeft: 4,
                }}
              />
            </div>

            <div
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 6,
              }}
            >
              {settingsGroups.map((group) => {
                const hasActive = (group.items || []).some((i) =>
                  isActive(i.path),
                );
                const groupColor = group.color || C.accent;

                return (
                  <button
                    key={group.key}
                    className="shimmer-item"
                    onClick={() => navigateTo("module", group.key)}
                    style={{
                      display: "flex",
                      flexDirection: "column",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: 5,
                      minHeight: 68,
                      padding: "10px 4px 8px",
                      borderRadius: 8,
                      border: hasActive
                        ? `1.5px solid ${groupColor}`
                        : `1.5px solid ${hexToRgba(groupColor, 0.8)}`,
                      background: hasActive
                        ? hexToRgba(groupColor, 0.3)
                        : hexToRgba(groupColor, 0.12),
                      boxShadow: hasActive
                        ? `0 0 14px ${hexToRgba(groupColor, 0.4)}`
                        : "none",
                      cursor: "pointer",
                      transition: "all 0.25s ease",
                    }}
                    onMouseEnter={(e) => {
                      const icon =
                        e.currentTarget.querySelector(".module-icon");
                      if (!hasActive) {
                        e.currentTarget.style.transform =
                          "translateY(-2px) scale(1.02)";
                        e.currentTarget.style.boxShadow = `0 0 16px ${hexToRgba(groupColor, 0.35)}`;
                        e.currentTarget.style.background = hexToRgba(
                          groupColor,
                          0.22,
                        );
                      }
                      if (icon) icon.style.transform = "scale(1.12)";
                    }}
                    onMouseLeave={(e) => {
                      const icon =
                        e.currentTarget.querySelector(".module-icon");
                      if (!hasActive) {
                        e.currentTarget.style.transform =
                          "translateY(0) scale(1)";
                        e.currentTarget.style.boxShadow = "none";
                        e.currentTarget.style.background = hexToRgba(
                          groupColor,
                          0.12,
                        );
                      }
                      if (icon) icon.style.transform = "scale(1)";
                    }}
                  >
                    <span
                      className="module-icon"
                      style={{
                        fontSize: 17,
                        color: groupColor,
                        display: "flex",
                        alignItems: "center",
                        lineHeight: 1,
                        transition: "transform 0.25s ease",
                      }}
                    >
                      {group.icon}
                    </span>

                    <span
                      style={{
                        fontSize: 10,
                        fontWeight: hasActive ? 700 : 600,
                        color: hasActive ? C.textActive : C.text,
                        fontFamily: "Roboto, sans-serif",
                        textAlign: "center",
                        lineHeight: 1.25,
                        wordBreak: "break-word",
                      }}
                    >
                      {group.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        <div
          style={{ height: 1, margin: "6px 12px", background: C.headerBorder }}
        />

        {/* Theme picker */}
        <div style={{ padding: "8px 10px 14px" }}>
          <ThemePicker
            activeThemeId={themeId}
            onSelect={handleThemeSelect}
            C={C}
          />
        </div>
      </div>
    );
  }

  if (screen === "module" && selectedModuleGroup) {
    const moduleReports = selectedModuleGroup.moduleReports || [];

    if (
      screen === "module" &&
      selectedModuleGroup &&
      INVENTORY_STYLE_MODULES.includes(selectedModule) &&
      selectedModuleGroup.subGroups
    ) {
      return (
        <div
          data-screen="module"
          className="sb-shimmer"
          style={{ ...screenStyle, height: "100%", padding: 0 }}
        >
          <InventoryScreen
            group={selectedModuleGroup}
            isActive={isActive}
            goBack={goBack}
            navigate={navigate}
            C={C}
            onReports={() => navigateTo("module-reports", selectedModule)}
            isReportsScreen={screen === "module-reports"}
          />
        </div>
      );
    }

    // Default module screen — used by Masters & Utilities (flat `items` list)
    return (
      <div
        data-screen="module"
        className="sb-shimmer"
        style={{ ...screenStyle, height: "100%" }}
      >
        <style>{SHIMMER_STYLE}</style>
        <DashboardStrip />
        <NavigationHeader
          title={selectedModuleGroup.label}
          showBack
          onBack={goBack}
          C={C}
        />

        <div
          className="scheme-reports-scroll"
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarWidth: "thin",
            scrollbarColor: "#e8a4de #f5f0ff",
            padding: "3px 3px 10px",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr",
              gap: 6,
            }}
          >
            {(selectedModuleGroup.items || []).map((item) => (
              <TileItem
                key={item.path}
                label={item.label}
                icon={item.icon || selectedModuleGroup.icon}
                path={item.path}
                moduleColor={selectedModuleGroup.color}
              />
            ))}
          </div>

          {moduleReports.length > 0 && (
            <div
              style={{
                marginTop: 8,
                paddingTop: 8,
                borderTop: `1px solid ${C.headerBorder}`,
              }}
            >
              <span
                style={{
                  display: "block",
                  fontSize: 9,
                  fontWeight: 700,
                  color: C.textMuted,
                  letterSpacing: "0.08em",
                  textTransform: "uppercase",
                  padding: "0 16px 4px",
                  fontFamily: "Roboto, sans-serif",
                }}
              >
                Reports
              </span>
              <ReportsAccordion entries={moduleReports} C={C} />
            </div>
          )}
        </div>
      </div>
    );
  }

  if (screen === "module-reports" && selectedModule) {
    const reportGroup = ALL_GROUPS.find((g) => g.key === selectedModule);
    const moduleReports = reportGroup?.moduleReports || [];
    const moduleColor = reportGroup?.color || "#4cdb5f";

    return (
      <div
        data-screen="module-reports"
        className="sb-shimmer"
        style={{
          ...screenStyle,
          height: "100%",
          padding: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <style>{SHIMMER_STYLE}</style>
        <style>{DASHBOARD_NAV_STYLE}</style>

        {/* Same dashboard strip as module screen */}
        <button
          className="dashboard-nav-btn"
          onClick={() => {
            navigate("/dashboard");
            goBack();
          }}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 9,
            width: "100%",
            padding: "5px 18px",
            background: isActive("/dashboard") ? "#B84A4A" : "#8B3A3A",
            border: "1px solid rgba(255, 255, 255, 0.2)",
            borderRadius: 7,
            cursor: "pointer",
            transition: "background 0.18s ease, transform 0.15s ease",
          }}
          onMouseEnter={(e) => {
            if (!isActive("/dashboard")) {
              e.currentTarget.style.background = "#9C4444";
            }
          }}
          onMouseLeave={(e) => {
            if (!isActive("/dashboard")) {
              e.currentTarget.style.background = "#8B3A3A";
            }
          }}
        >
          <span
            className="dashboard-nav-icon"
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 30,
              height: 25,
              borderRadius: 6,
              background: isActive("/dashboard")
                ? "rgba(255, 255, 255, 0.25)"
                : "rgba(255, 255, 255, 0.15)",
              color: "#FFFFFF",
              fontSize: 15,
              flexShrink: 0,
            }}
          >
            <HomeOutlined />
          </span>
          <span
            style={{
              flex: 1,
              fontSize: 14,
              fontWeight: 700,
              color: "#FFFFFF",
              fontFamily: "Roboto, sans-serif",
              textAlign: "left",
            }}
          >
            Go To Home
          </span>
          <span
            className="dashboard-nav-arrow"
            style={{
              display: "flex",
              alignItems: "center",
              color: isActive("/dashboard") ? "#FFFFFF" : "#F0D9D9",
              fontSize: 11,
              flexShrink: 0,
            }}
          >
            <RightOutlined />
          </span>
        </button>

        {/* Title bar — back button + module badge */}
        <div
          style={{
            height: 44,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            gap: 4,
            padding: "0 10px",
            background: "rgba(0,0,0,0.2)",
            borderTop: "1px solid rgba(255,255,255,0.2)",
            borderBottom: "1px solid rgba(255,255,255,0.2)",
            boxShadow: "0 1px 0 rgba(255,255,255,0.06)",
          }}
        >
          {/* Back → module screen */}
          <button
            onClick={() => navigateTo("module", selectedModule)}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              width: 26,
              height: 26,
              borderRadius: 6,
              border: "1px solid rgba(255,255,255,0.25)",
              background: moduleColor,
              cursor: "pointer",
              flexShrink: 0,
              color: C.text,
              fontSize: 10,
            }}
            aria-label="Back to module"
          >
            <span
              className="dashboard-nav-arrow"
              style={{
                color: "#fff",
                fontSize: 11,
                flexShrink: 0,
                fontWeight: 700,
              }}
            >
              <LeftOutlined />
            </span>
          </button>

          {/* Module color badge */}
          <div
            style={{
              background: moduleColor,
              padding: "5px 3px",
              borderRadius: 6,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
              minWidth: 80,
              width: 140,
            }}
          >
            <span
              style={{
                fontWeight: 700,
                fontSize: 12,
                color: "#000000",
                fontFamily: "Roboto, sans-serif",
                letterSpacing: "0.1em",
                textTransform: "uppercase",
                whiteSpace: "nowrap",
              }}
            >
              {reportGroup?.label || selectedModule}
            </span>
          </div>
        </div>

        {/* Scrollable accordion list */}
        <SectionHeading label={"Reports"} moduleColor={moduleColor} C={C} />
        <div
          className="scheme-reports-scroll"
          style={{
            flex: 1,
            overflowY: "auto",
            overflowX: "hidden",
            scrollbarWidth: "thin",
            scrollbarColor: "#e8a4de #f5f0ff",
            padding: "8px 6px",
            minHeight: 0,
            background: "rgba(0,0,0,0.15)",
          }}
        >
          {moduleReports.length === 0 ? (
            <div
              style={{
                textAlign: "center",
                padding: "24px 16px",
                color: C.textMuted,
                fontSize: 12,
                fontFamily: "Roboto, sans-serif",
              }}
            >
              No reports available for this module.
            </div>
          ) : (
            moduleReports.map((entry) => (
              <ReportCategoryAccordion
                key={entry.key}
                entry={entry}
                moduleColor={moduleColor}
                isActive={isActive}
                C={C}
                openAccordion={openAccordion}
                toggleAccordion={toggleAccordion}
              />
            ))
          )}
        </div>
        <style jsx>{`
          ::-webkit-scrollbar {
            width: 4px;
          }
          ::-webkit-scrollbar-track {
            background: #f5f0ff;
            border-radius: 4px;
          }
          ::-webkit-scrollbar-thumb {
            background: #e8a4de;
            border-radius: 4px;
          }
        `}</style>
      </div>
    );
  }

  return null;
};

export default Sidebar;